import { resolveTileId, resolveTileSetId } from "../engine/tileCatalog.js";
export const MAP_TEXT_LABEL_FONT_FAMILIES = [
    "classic",
    "fondamento",
];
export const DEFAULT_MAP_TEXT_LABEL_FONT_FAMILY = "fondamento";
export const EMPTY_EDITOR_PERSISTENCE_STATE = {
    history: [],
    historyIndex: -1,
};
export const MAP_DOCUMENT_VERSION = 2;
function normalizeHexData(hex) {
    return {
        ...hex,
        tileSetId: resolveTileSetId({
            tileId: hex.tileId,
            tileFilename: hex.tileFilename,
            tileSetId: hex.tileSetId,
        }) ?? hex.tileSetId,
        tileId: resolveTileId({
            tileId: hex.tileId,
            tileFilename: hex.tileFilename,
            tileSetId: hex.tileSetId,
        }) ?? hex.tileId,
        tileFilename: undefined,
    };
}
function normalizeHexRecord(record) {
    return Object.fromEntries(Object.entries(record).map(([key, hex]) => [key, normalizeHexData(hex)]));
}
function normalizeDecorationRecord(record) {
    if (!record) {
        return {};
    }
    return Object.fromEntries(Object.entries(record)
        .filter(([, decoration]) => Boolean(decoration))
        .map(([key, decoration]) => [
        key,
        {
            id: decoration.id,
            decorationId: decoration.decorationId,
            q: decoration.q,
            r: decoration.r,
            offsetX: decoration.offsetX,
            offsetY: decoration.offsetY,
        },
    ]));
}
function normalizeTextLabelRecord(record) {
    if (!record) {
        return {};
    }
    return Object.fromEntries(Object.entries(record)
        .filter(([, label]) => Boolean(label))
        .map(([key, label]) => [
        key,
        {
            id: label.id,
            x: label.x,
            y: label.y,
            text: label.text,
            fontSize: label.fontSize,
            fontFamily: normalizeMapTextLabelFontFamily(label.fontFamily),
            color: label.color,
            bold: label.bold,
            italic: label.italic,
            rotation: label.rotation ?? 0,
            banner: typeof label.banner === "boolean" ? label.banner : true,
        },
    ]));
}
function normalizeSnapshotCandidate(value) {
    if (!value || typeof value !== "object") {
        return {};
    }
    const candidate = value;
    if ("hexes" in candidate || "decorations" in candidate || "textLabels" in candidate) {
        const next = {};
        if (candidate.hexes) {
            next.hexes = normalizeHexRecord(candidate.hexes);
        }
        if (candidate.decorations) {
            next.decorations = Object.fromEntries(Object.entries(candidate.decorations).map(([id, decoration]) => [
                id,
                decoration
                    ? {
                        id: decoration.id,
                        decorationId: decoration.decorationId,
                        q: decoration.q,
                        r: decoration.r,
                        offsetX: decoration.offsetX,
                        offsetY: decoration.offsetY,
                    }
                    : null,
            ]));
        }
        if (candidate.textLabels) {
            next.textLabels = Object.fromEntries(Object.entries(candidate.textLabels).map(([id, label]) => [
                id,
                label
                    ? {
                        id: label.id,
                        x: label.x,
                        y: label.y,
                        text: label.text,
                        fontSize: label.fontSize,
                        fontFamily: normalizeMapTextLabelFontFamily(label.fontFamily),
                        color: label.color,
                        bold: label.bold,
                        italic: label.italic,
                        rotation: label.rotation ?? 0,
                        banner: typeof label.banner === "boolean" ? label.banner : true,
                    }
                    : null,
            ]));
        }
        return next;
    }
    return {
        hexes: normalizeHexRecord(value),
    };
}
function normalizeHistoryEntry(entry) {
    return {
        before: normalizeSnapshotCandidate(entry.before),
        after: normalizeSnapshotCandidate(entry.after),
    };
}
export function normalizeMapTextLabelFontFamily(value) {
    return MAP_TEXT_LABEL_FONT_FAMILIES.includes(value)
        ? value
        : DEFAULT_MAP_TEXT_LABEL_FONT_FAMILY;
}
function normalizeEditorPersistenceState(state) {
    const history = Array.isArray(state?.history) ? state.history.map(normalizeHistoryEntry) : [];
    const maxIndex = history.length - 1;
    const historyIndex = typeof state?.historyIndex === "number" && Number.isInteger(state.historyIndex)
        ? Math.min(Math.max(state.historyIndex, -1), maxIndex)
        : maxIndex;
    return {
        history,
        historyIndex,
    };
}
function isSerializedMapState(value) {
    if (!value || typeof value !== "object") {
        return false;
    }
    const candidate = value;
    return (Boolean(candidate.bounds) &&
        typeof candidate.bounds.halfCols === "number" &&
        typeof candidate.bounds.halfRows === "number" &&
        Boolean(candidate.hexes) &&
        typeof candidate.hexes === "object" &&
        Boolean(candidate.partyPosition) &&
        typeof candidate.partyPosition === "object");
}
function isLegacySerializedMapStateV1(value) {
    if (!value || typeof value !== "object") {
        return false;
    }
    const candidate = value;
    return (typeof candidate.radius === "number" &&
        Boolean(candidate.hexes) &&
        typeof candidate.hexes === "object" &&
        Boolean(candidate.partyPosition) &&
        typeof candidate.partyPosition === "object");
}
function isMapDocument(value) {
    if (!value || typeof value !== "object") {
        return false;
    }
    const candidate = value;
    return candidate.version === MAP_DOCUMENT_VERSION && isSerializedMapState(candidate.map);
}
function isLegacyMapDocumentV1(value) {
    if (!value || typeof value !== "object") {
        return false;
    }
    const candidate = value;
    return candidate.version === 1 && isLegacySerializedMapStateV1(candidate.map);
}
// Convert a legacy `radius` (hexagon half-extent) into rectangular bounds that
// fully enclose the original hexagon. Using halfCols = halfRows = radius keeps
// world bounds and existing hex coordinates intact, so older data still loads
// without losing tiles or breaking the camera clamp.
function radiusToBounds(radius) {
    const safe = Number.isFinite(radius) && radius > 0 ? Math.round(radius) : 0;
    return { halfCols: safe, halfRows: safe };
}
function migrateLegacySerializedMapStateV1(data) {
    return {
        bounds: radiusToBounds(data.radius),
        hexes: data.hexes,
        decorations: data.decorations,
        textLabels: data.textLabels,
        partyPosition: data.partyPosition,
    };
}
export function serializeMapState(state) {
    const hexes = normalizeHexRecord(state.hexes);
    return {
        bounds: { halfCols: state.bounds.halfCols, halfRows: state.bounds.halfRows },
        hexes,
        decorations: normalizeDecorationRecord(state.decorations),
        textLabels: normalizeTextLabelRecord(state.textLabels),
        partyPosition: state.partyPosition,
    };
}
export function deserializeMapState(data) {
    const hexes = normalizeHexRecord(data.hexes);
    return {
        bounds: { halfCols: data.bounds.halfCols, halfRows: data.bounds.halfRows },
        hexes,
        decorations: normalizeDecorationRecord(data.decorations),
        textLabels: normalizeTextLabelRecord(data.textLabels),
        partyPosition: data.partyPosition,
    };
}
export function createMapDocument(mapState, editorState = EMPTY_EDITOR_PERSISTENCE_STATE) {
    return {
        version: MAP_DOCUMENT_VERSION,
        savedAt: new Date().toISOString(),
        map: serializeMapState(mapState),
        editor: normalizeEditorPersistenceState(editorState),
    };
}
export function normalizeMapDocument(data) {
    const imported = importMapDocument(data);
    return createMapDocument(imported.mapState, imported.editorState);
}
export function importMapDocument(data) {
    if (isMapDocument(data)) {
        return {
            mapState: deserializeMapState(data.map),
            editorState: normalizeEditorPersistenceState(data.editor),
        };
    }
    // v1 document → migrate radius to bounds, then deserialize as v2.
    if (isLegacyMapDocumentV1(data)) {
        return {
            mapState: deserializeMapState(migrateLegacySerializedMapStateV1(data.map)),
            editorState: normalizeEditorPersistenceState(data.editor),
        };
    }
    if (isSerializedMapState(data)) {
        return {
            mapState: deserializeMapState(data),
            editorState: EMPTY_EDITOR_PERSISTENCE_STATE,
        };
    }
    // Bare legacy v1 map state (no document wrapper).
    if (isLegacySerializedMapStateV1(data)) {
        return {
            mapState: deserializeMapState(migrateLegacySerializedMapStateV1(data)),
            editorState: EMPTY_EDITOR_PERSISTENCE_STATE,
        };
    }
    throw new Error("Unsupported map file format.");
}
export function importLegacyPersistedMapStore(data) {
    if (!data || typeof data !== "object") {
        return null;
    }
    const persistedStore = data;
    const mapState = persistedStore.state?.mapState;
    if (!mapState) {
        return null;
    }
    if (isSerializedMapState(mapState)) {
        return {
            mapState: deserializeMapState(mapState),
            editorState: EMPTY_EDITOR_PERSISTENCE_STATE,
        };
    }
    if (isLegacySerializedMapStateV1(mapState)) {
        return {
            mapState: deserializeMapState(migrateLegacySerializedMapStateV1(mapState)),
            editorState: EMPTY_EDITOR_PERSISTENCE_STATE,
        };
    }
    return null;
}
export function importMapFromJson(json) {
    return importMapDocument(JSON.parse(json));
}
